import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { gradients } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export enum NotificationBadgeVariant {
  Notification = 'notification',
  Info = 'info',
  FilterHover = 'filterHover',
  Popup = 'popup',
  New = 'new',
  Beta = 'beta',
}

export type NotificationBadgeSize = Size.xs | Size.sm | Size.md | 'dot'

const NotificationBadgeParts = [
  'container',
  'contentContainer',
  'text',
] as const

const helpers = createMultiStyleConfigHelpers(NotificationBadgeParts)

export const NotificationBadgeStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'inline-flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '6.25rem',
    },
    text: {
      ...textStyles[textStyleKeys.Body4_Bold],
    },
  },
  variants: {
    [NotificationBadgeVariant.Notification]: {
      contentContainer: {
        bg: 'brand.solid',
      },
      text: {
        color: 'brand.contrast',
      },
    },
    [NotificationBadgeVariant.Info]: {
      contentContainer: {
        bg: 'bg.inverted',
      },
      text: {
        color: 'fg.inverted',
      },
    },
    [NotificationBadgeVariant.FilterHover]: {
      contentContainer: {
        bg: 'brand.solid',
      },
      text: {
        color: 'brand.contrast',
      },
    },
    [NotificationBadgeVariant.Popup]: {
      contentContainer: {
        backgroundImage: gradients.mangoBrand.button,
      },
      text: {
        color: 'brand.contrast',
      },
    },
    [NotificationBadgeVariant.New]: {
      contentContainer: {
        bg: 'green.solid',
      },
      text: {
        color: 'green.contrast',
      },
    },
    [NotificationBadgeVariant.Beta]: {
      contentContainer: {
        bg: 'mango.orange',
      },
      text: {
        color: 'brand.contrast',
      },
    },
  },
  sizes: {
    [Size.md]: {
      contentContainer: {
        minWidth: '1.5rem',
        height: '1.5rem',
        p: '0.25rem',
      },
      text: {
        lineHeight: '1.5rem',
      },
    },
    [Size.sm]: {
      contentContainer: {
        minWidth: '1.25rem',
        height: '1.25rem',
        px: '0.25rem',
        py: '0.125rem',
      },
      text: {
        lineHeight: '1.25rem',
      },
    },
    [Size.xs]: {
      contentContainer: {
        minWidth: '.95rem',
        height: '.95rem',
        px: '0.1rem',
        py: '0.075rem',
      },
      text: {
        lineHeight: '1.25rem',
      },
    },
    dot: {
      contentContainer: {
        width: '0.5rem',
        height: '0.5rem',
        minWidth: '0.5rem',
        p: '0',
      },
      text: {
        display: 'none',
      },
    },
  },
  defaultProps: {
    variant: NotificationBadgeVariant.Notification,
    size: Size.md,
  },
})

export const useNotificationBadgeStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(NotificationBadgeStyle, props) as MultiStyleType<
    typeof NotificationBadgeParts
  >
}
